import { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../../../GlobalStore/GlobalStore.ts';
import { writeSettings } from '../../../Utils/db/users/userSettings/writeSettings.ts';
import { MdOutlineArrowDropDown } from 'react-icons/md';
import { GlobalContext } from '../../../App.tsx';
import { getModeIcon, getModeText } from './modeConfig.ts';
import { useClickOutside, useDropdownPositioning } from './useDropdown.ts';
import ModeDropdown from './ModeDropdown.tsx';

const ModeSelector = () => {
    const { editorRef } = useContext(GlobalContext);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);

    const { mode, theme, customModes = {} } = settingsObj;
    const { environment } = environmentObj;

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [hoveredMode, setHoveredMode] = useState<string | null>(null);
    const [portalElement] = useState(() => document.createElement('div'));
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Use custom hooks for dropdown functionality
    const popupRef = useClickOutside({
        isOpen: isPopupOpen,
        setIsOpen: setIsPopupOpen,
        buttonRef
    });
    const position = useDropdownPositioning(buttonRef, isPopupOpen);

    const ModeIcon = getModeIcon(mode, customModes);
    const modeText = getModeText(mode, environment, customModes);

    useEffect(() => {
        document.body.appendChild(portalElement);
        return () => {
            document.body.removeChild(portalElement);
        };
    }, [portalElement]);

    const handleModeChange = (newMode: string) => {
        // Prevent switching to FSD mode if API key mode is enabled
        if (newMode === 'fsd' && settingsObj.apiKeyOnly) {
            // Show warning popup and don't switch modes
            setDynamicStateObj({ visiblePopup: 'apiKeyModeWarning' });
            setIsPopupOpen(false);
            return;
        }

        if (userObj.user) {
            writeSettings(userObj.user, { mode: newMode }, environment);
        }
        setSettingsObj({ mode: newMode });
        setDynamicStateObj({ currentMode: newMode });
        setIsPopupOpen(false);
        editorRef.current?.focus();
    };

    const handleAddModeClick = () => {
        // Close the mode dropdown
        setIsPopupOpen(false);
        // Open settings menu directly to Customization tab
        setDynamicStateObj({ visiblePopup: 'settings:Customization' });
    };

    const getItemBackground = (itemMode: string) => {
        if (hoveredMode === itemMode) {
            return 'bg-neutral-300 dark:bg-neutral-800';
        }
        return 'bg-neutral-100 dark:bg-neutral-900';
    };

    return (
        <>
            <button
                ref={buttonRef}
                onClick={() => setIsPopupOpen(!isPopupOpen)}
                className="flex items-center space-x-1 pl-3 pr-2 py-1.5 bg-gray-200 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 rounded-full hover:bg-gray-300 dark:hover:bg-neutral-700 text-xs font-medium"
            >
                <ModeIcon size={16} className={environment !== 'chrome' ? 'mr-1' : ''} />
                {(environment !== 'chrome') && <span>{modeText}</span>}
                <MdOutlineArrowDropDown size={20} className={`transition-transform duration-200 ${isPopupOpen ? 'rotate-180' : ''}`} />
            </button>
            {createPortal(
                <ModeDropdown
                    ref={popupRef}
                    position={position}
                    isOpen={isPopupOpen}
                    theme={theme}
                    currentMode={mode}
                    environment={environment}
                    customModes={customModes}
                    onModeChange={handleModeChange}
                    onMouseEnter={setHoveredMode}
                    onMouseLeave={() => setHoveredMode(null)}
                    getItemBackground={getItemBackground}
                    onAddModeClick={handleAddModeClick}
                />,
                portalElement
            )}
        </>
    );
};

export default ModeSelector;
